import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { ApiError } from "@jobenvoy/core";
import { useGate } from "@/components/site/gateContext";
import { trackControl } from "@/lib/analytics";
import { describeError } from "@/lib/api/client";
import { tailorPastedJob, type TailorRequested } from "@/lib/api/envoy";
import type { ResumeVersion } from "@/lib/api/resumeService";
import type { PastedJob } from "@/lib/tools/pastedJob";
import {
    hasWorkHistory,
    isLinkRefusal,
    LINK_REFUSED,
    nextStep,
    postingRefusal,
    RESUME_EMPTY,
    TAILOR_SLOW,
    TAILOR_STOPPED,
    TAILOR_UNREACHED,
    waitingLine,
    type KeptJob,
} from "@/lib/tools/tailorAnswer";
import { TAILOR_WATCH_MS, useTailoredApplication, writtenResume } from "./envoy";
import { keys } from "./keys";
import { useBaselineNow } from "./resume";
import { ONE_AT_A_TIME, useSingleFlightMutation } from "./singleFlight";

export type ToolTailorStep = "idle" | "upload" | "tailoring" | "ready" | "failed";

/** Inside `tailoring`: the saved resume being read, the job on its way, the tailor at work. */
export type ToolTailorStage = "reading" | "sending" | "writing";

export interface ToolTailor {
    /** Takes a job to the tailor. A press while one is under way sends nothing. */
    start: (job: PastedJob) => void;
    step: ToolTailorStep;
    /** Null outside `tailoring`. Nothing has reached the tailor until `writing`. */
    stage: ToolTailorStage | null;
    line: string | null;
    resumeId: string | null;
    /** The upload dialog saved a resume: the job waiting for one goes to the tailor. */
    saved: () => void;
    /** The upload dialog went with nothing saved, and `line` says why when it did not close. */
    closed: (line?: string) => void;
    /** The job back for the form: its link refused, or its upload closed. */
    keptJob: KeptJob | null;
}

/** What the hook holds. A written resume or a failed one is read off the application. */
interface Run {
    step: "idle" | "upload" | "tailoring" | "failed";
    /** The saved resume is still being read, so nothing is known of what comes next. */
    reading: boolean;
    line: string | null;
    /** The posting being watched, once envoy has named it. */
    postingId: string | null;
    keptJob: KeptJob | null;
}

const IDLE: Run = { step: "idle", reading: false, line: null, postingId: null, keptJob: null };
const TAILORING: Run = { ...IDLE, step: "tailoring" };
const READING: Run = { ...TAILORING, reading: true };
const failed = (line: string): Run => ({ ...IDLE, step: "failed", line });

/**
 * A job to a finished resume, for a signed-in person: a pasted one, or a
 * posting from the catalogue. With no saved resume it stops at the upload
 * dialog and goes on once one is saved. The tailor reads the saved resume,
 * so the text in the resume box is never sent. It costs the person a
 * tailor, so one press is one request: `busy` is taken at the press, ahead
 * of any render, and let go only when a run has ended. Nothing is left to
 * wait without end: a run that passes its limit is over, and `runNumber`
 * stops a read that answers after that from sending what the person was
 * told did not go.
 */
export function useToolTailor(): ToolTailor {
    const { ask } = useGate();
    const { pathname } = useLocation();
    const client = useQueryClient();
    const { refetch: readBaseline } = useBaselineNow();
    const [run, setRun] = useState<Run>(IDLE);
    const busy = useRef(false);
    const runNumber = useRef(0);
    /** The job waiting for a saved resume. */
    const waitingJob = useRef<PastedJob | null>(null);
    const toldDone = useRef<string | null>(null);

    const toUpload = (job: PastedJob) => {
        waitingJob.current = job;
        setRun({ ...IDLE, step: "upload" });
    };

    const answered = (answer: TailorRequested) => {
        // A tailor was asked for, so the tailors left and the Applications page change.
        void client.invalidateQueries({ queryKey: keys.billing.all });
        if (answer.waiting_on === "OUT_OF_TAILORS") {
            ask("upgrade");
            busy.current = false;
            setRun(IDLE);
        } else if (answer.waiting_on || answer.status === "FAILED") {
            setRun(failed(waitingLine(answer) ?? TAILOR_STOPPED));
        } else {
            // This job's last run must not answer for this one.
            client.removeQueries({ queryKey: keys.envoy.applicationOf(answer.posting_id) });
            setRun({ ...TAILORING, postingId: answer.posting_id });
        }
        void client.invalidateQueries({ queryKey: keys.envoy.all });
    };

    const refused = async (error: Error, job: PastedJob) => {
        if (error instanceof ApiError && error.status === 412) {
            const read = await readBaseline();
            if (!read.isError && !read.data) return toUpload(job);
        }
        if (job.posting) {
            setRun(failed(postingRefusal(error) ?? describeError(error)));
        } else if (isLinkRefusal(error)) {
            setRun({ ...failed(LINK_REFUSED), keptJob: { job, problem: LINK_REFUSED } });
        } else {
            setRun(failed(describeError(error)));
        }
    };

    const tailor = useSingleFlightMutation(
        {
            mutationFn: tailorPastedJob,
            onSuccess: answered,
            onError: refused,
            // Offline it fails now, in words. Paused, it would go out once the person is
            // back online, with no press, after they were told it had not.
            networkMode: "always",
            // One press is one request, whatever a library's default comes to be.
            retry: false,
        },
        ONE_AT_A_TIME,
    );

    /** The tailor costs a credit, so a resume with nothing in it is stopped here. */
    const send = (job: PastedJob, baseline: ResumeVersion | null | undefined) => {
        if (!hasWorkHistory(baseline?.resume)) {
            setRun(failed(RESUME_EMPTY));
            return;
        }
        setRun(TAILORING);
        tailor.mutate(job);
    };

    const start = (job: PastedJob) => {
        if (busy.current) return;
        busy.current = true;
        runNumber.current += 1;
        const thisRun = runNumber.current;
        setRun(READING);
        void readBaseline().then((read) => {
            if (thisRun !== runNumber.current) return;
            if (read.isError) {
                const keptJob = { job, problem: null };
                setRun({ ...failed(describeError(read.error)), keptJob });
            } else if (nextStep(Boolean(read.data)) === "upload") toUpload(job);
            else send(job, read.data);
        });
    };

    const saved = () => {
        const job = waitingJob.current;
        if (!job) return;
        waitingJob.current = null;
        send(job, client.getQueryData<ResumeVersion | null>(keys.resume.baseline));
    };

    const closed = (line?: string) => {
        const job = waitingJob.current;
        if (!job) return;
        waitingJob.current = null;
        busy.current = false;
        const keptJob = { job, problem: null };
        setRun(line ? { ...failed(line), keptJob } : { ...IDLE, keptJob });
    };

    const watching = run.step === "tailoring" ? run.postingId : null;
    const application = useTailoredApplication(watching).data;
    const stopped = watching !== null && application?.status === "FAILED";
    const resumeId = watching ? writtenResume(application) : null;
    const step = stopped ? "failed" : resumeId ? "ready" : run.step;
    const stage =
        step !== "tailoring" ? null : run.reading ? "reading" : watching ? "writing" : "sending";

    useEffect(() => {
        if (step === "ready" || step === "failed") busy.current = false;
    }, [step]);

    useEffect(() => {
        if (step !== "tailoring") return;
        const timer = setTimeout(() => {
            runNumber.current += 1;
            setRun(failed(watching ? TAILOR_SLOW : TAILOR_UNREACHED));
        }, TAILOR_WATCH_MS);
        return () => clearTimeout(timer);
    }, [step, watching]);

    useEffect(() => {
        if (!resumeId || toldDone.current === resumeId) return;
        toldDone.current = resumeId;
        trackControl("tool_tailor_done", { source: "ResumeCheck", target: pathname });
    }, [resumeId, pathname]);

    return {
        start,
        step,
        stage,
        line: stopped ? TAILOR_STOPPED : run.line,
        resumeId,
        saved,
        closed,
        keptJob: run.keptJob,
    };
}
